import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MonitorRow } from '@ecinsa/shared';
import { api, type FichaOrden } from '../lib/api';
import { useTerminal } from '../lib/store';
import { SinDatos } from '../components/charts';
import { Dot, LiveTimer, Pill } from '../components/ui';
import { clockTime, hhmm, qty, shortDate } from '../lib/format';

/** Eventos que cuentan como «alguien está delante». */
const TOQUES = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/** Días enteros entre hoy y una fecha AAAA-MM-DD; negativo si ya pasó. */
function diasHasta(fecha: string, hoy: Date): number {
  const d = new Date(`${fecha}T00:00:00`);
  const h = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((d.getTime() - h.getTime()) / 86_400_000);
}

function textoEntrega(fecha: string | null, hoy: Date): string {
  if (!fecha) return 'sin fecha de entrega';
  const dias = diasHasta(fecha, hoy);
  if (dias === 0) return 'entrega hoy';
  if (dias === 1) return 'entrega mañana';
  if (dias === -1) return 'tenía que entregarse ayer';
  return dias > 0 ? `entrega en ${dias} días` : `con ${-dias} días de retraso`;
}

/**
 * La OF que se enseña cuando nadie toca nada: la de entrega más cercana a hoy,
 * por delante o por detrás. A igual distancia gana la que aún está a tiempo
 * (todavía se puede hacer algo por ella) y después la que tiene gente dentro.
 */
function ofDeOrigen(filas: MonitorRow[], hoy: Date): MonitorRow | null {
  const liberadas = filas.filter((r) => r.status === 'released');
  const candidatas = liberadas.length > 0 ? liberadas : filas;
  const conFecha = candidatas.filter((r) => r.dueDate);
  if (conFecha.length === 0) return candidatas[0] ?? null;
  return [...conFecha].sort((a, b) => {
    const da = diasHasta(a.dueDate!, hoy);
    const db = diasHasta(b.dueDate!, hoy);
    return (
      Math.abs(da) - Math.abs(db) ||
      Number(da < 0) - Number(db < 0) ||
      b.activeOperators.length - a.activeOperators.length ||
      a.docNum.localeCompare(b.docNum)
    );
  })[0]!;
}

/** Hora del tramo; si no es de hoy, con el día delante. */
function desdeLegible(iso: string, hoy: Date): string {
  const d = new Date(iso);
  const hora = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' }).format(d);
  return d.toDateString() === hoy.toDateString() ? hora : `${shortDate(iso)} ${hora}`;
}

/**
 * Monitor por OF: quién está dentro de una orden ahora mismo.
 *
 * Por defecto enseña sola la OF con la entrega más cercana a hoy, que es la
 * que el taller tiene que mirar. Cualquiera puede pedir otra desde el
 * selector; en cuanto pasan unos segundos sin que nadie toque ratón, teclado
 * ni pantalla, vuelve a la de origen. Así la pantalla colgada nunca se queda
 * enseñando la OF que alguien consultó hace una hora y nadie devolvió.
 */
export function MonitorOF() {
  const { catalog } = useTerminal();
  const navigate = useNavigate();
  const [filas, setFilas] = useState<MonitorRow[]>([]);
  const [ficha, setFicha] = useState<FichaOrden | null>(null);
  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [elegida, setElegida] = useState<number | null>(null);
  const [consulta, setConsulta] = useState('');
  const [selectorAbierto, setSelectorAbierto] = useState(false);
  const [reloj, setReloj] = useState(new Date());
  const [live, setLive] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const ultimoToque = useRef(Date.now());
  const entrada = useRef<HTMLInputElement>(null);

  const refreshSeconds = Number(catalog?.parameters['monitor.refreshSec'] || 10);
  const idleSeconds = Math.max(5, Number(catalog?.parameters['monitor.ofIdleSec'] || 30));

  const dia = reloj.toDateString();
  const origen = useMemo(() => ofDeOrigen(filas, new Date()), [filas, dia]);
  const visible = elegida ?? origen?.docEntry ?? null;
  const enOrigen = elegida === null || elegida === origen?.docEntry;

  // El socket y el temporizador necesitan saber qué OF recargar sin tener que
  // volver a montarse cada vez que cambia.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  const cargarTablero = useCallback(async () => {
    try {
      const board = await api.board();
      setFilas(board.rows);
      setUpdatedAt(new Date());
    } catch {
      // Se queda lo último bueno; el «actualizado a las…» deja de avanzar.
    }
  }, []);

  const cargarFicha = useCallback(async (docEntry: number | null) => {
    if (docEntry === null) return;
    try {
      const f = await api.ficha(docEntry);
      if (visibleRef.current === docEntry) setFicha(f);
    } catch {
      if (visibleRef.current === docEntry) setFicha(null);
    } finally {
      if (visibleRef.current === docEntry) setCargandoFicha(false);
    }
  }, []);

  const recargar = useCallback(() => {
    void cargarTablero();
    void cargarFicha(visibleRef.current);
  }, [cargarTablero, cargarFicha]);

  useEffect(() => {
    void cargarTablero();
    const t = setInterval(recargar, Math.max(5, refreshSeconds) * 1000);
    return () => clearInterval(t);
  }, [cargarTablero, recargar, refreshSeconds]);

  // Al cambiar de OF se limpia lo de la anterior: enseñar la gente de una OF
  // bajo la cabecera de otra, aunque sea un segundo, es mentir.
  useEffect(() => {
    setFicha(null);
    setCargandoFicha(true);
    void cargarFicha(visible);
  }, [visible, cargarFicha]);

  useEffect(() => {
    const t = setInterval(() => setReloj(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const protocol = location.protocol === 'https:' ? 'wss' : 'ws';
    let socket: WebSocket | null = null;
    let retry: number | undefined;
    const connect = () => {
      socket = new WebSocket(`${protocol}://${location.host}/api/monitor/ws`);
      socket.onopen = () => setLive(true);
      socket.onmessage = () => recargar();
      socket.onclose = () => {
        setLive(false);
        retry = window.setTimeout(connect, 5000);
      };
      socket.onerror = () => socket?.close();
    };
    connect();
    return () => {
      window.clearTimeout(retry);
      if (socket) socket.onclose = null;
      socket?.close();
    };
  }, [recargar]);

  // Cualquier toque reinicia la cuenta atrás. Va en una referencia y no en el
  // estado: mover el ratón no debe repintar la pantalla cien veces por segundo.
  useEffect(() => {
    const tocar = () => {
      ultimoToque.current = Date.now();
    };
    for (const e of TOQUES) window.addEventListener(e, tocar, { passive: true });
    return () => {
      for (const e of TOQUES) window.removeEventListener(e, tocar);
    };
  }, []);

  const hayAlgoQueDeshacer = !enOrigen || consulta !== '' || selectorAbierto;
  const quedan = Math.max(0, idleSeconds - Math.floor((reloj.getTime() - ultimoToque.current) / 1000));

  useEffect(() => {
    if (!hayAlgoQueDeshacer || quedan > 0) return;
    setElegida(null);
    setConsulta('');
    setSelectorAbierto(false);
    entrada.current?.blur();
  }, [hayAlgoQueDeshacer, quedan]);

  /** OFs para el selector y para pasar con ‹ ›: liberadas primero, por entrega. */
  const ordenadas = useMemo(
    () =>
      [...filas].sort(
        (a, b) =>
          Number(a.status !== 'released') - Number(b.status !== 'released') ||
          (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999') ||
          a.docNum.localeCompare(b.docNum),
      ),
    [filas],
  );

  const sugerencias = useMemo(() => {
    const q = consulta.trim().toLowerCase();
    const lista = q
      ? ordenadas.filter((r) =>
          [r.docNum, r.itemCode, r.itemDescription, r.customerName ?? '', r.project ?? ''].some((campo) =>
            campo.toLowerCase().includes(q),
          ),
        )
      : ordenadas;
    return lista.slice(0, 40);
  }, [ordenadas, consulta]);

  const elegir = (docEntry: number) => {
    setElegida(docEntry);
    setConsulta('');
    setSelectorAbierto(false);
    entrada.current?.blur();
  };

  const pasar = (salto: number) => {
    if (ordenadas.length === 0) return;
    const i = ordenadas.findIndex((r) => r.docEntry === visible);
    const n = ordenadas.length;
    const siguiente = ordenadas[(((i < 0 ? 0 : i + salto) % n) + n) % n];
    if (siguiente) elegir(siguiente.docEntry);
  };

  const fila = filas.find((r) => r.docEntry === visible) ?? null;
  const orden = ficha?.orden ?? null;
  const dentro = useMemo(
    () =>
      (ficha?.imputaciones ?? [])
        .filter((t) => t.estado === 'running')
        .sort((a, b) => a.desde.localeCompare(b.desde)),
    [ficha],
  );
  const salidasHoy = useMemo(
    () =>
      (ficha?.imputaciones ?? [])
        .filter((t) => t.estado !== 'running' && new Date(t.desde).toDateString() === dia)
        .slice(0, 8),
    [ficha, dia],
  );
  const otrasConGente = ordenadas.filter((r) => r.docEntry !== visible && r.activeOperators.length > 0);
  const ultimo = ficha?.imputaciones[0] ?? null;

  const entrega = orden?.dueDate ?? fila?.dueDate ?? null;
  const tonoEntrega = fila?.delivery === 'late' ? 'danger' : fila?.delivery === 'soon' ? 'warn' : 'ok';

  const segundosSinActualizar = updatedAt ? Math.round((reloj.getTime() - updatedAt.getTime()) / 1000) : null;
  const obsoleto = segundosSinActualizar !== null && segundosSinActualizar > Math.max(60, refreshSeconds * 3);

  return (
    <div className="app app--escritorio monitor">
      <header className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate('/monitor')} aria-label="Volver al monitor">
          ←
        </button>
        <div>
          <div className="topbar__title">Quién está en la OF</div>
          <div className="topbar__sub">{updatedAt ? `Actualizado a las ${clockTime(updatedAt)}` : 'Cargando…'}</div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn btn--ghost" onClick={() => pasar(-1)} aria-label="OF anterior">‹</button>
          <button className="btn btn--ghost" onClick={() => pasar(1)} aria-label="OF siguiente">›</button>
        </div>
        <div className="monitorof__selector">
          <input
            ref={entrada}
            className="input"
            value={consulta}
            onChange={(e) => {
              setConsulta(e.target.value);
              setSelectorAbierto(true);
            }}
            onFocus={() => setSelectorAbierto(true)}
            // Se espera un poco antes de cerrar: si no, el clic en una opción
            // llega cuando la lista ya no está.
            onBlur={() => window.setTimeout(() => setSelectorAbierto(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && sugerencias[0]) elegir(sugerencias[0].docEntry);
              if (e.key === 'Escape') {
                setConsulta('');
                setSelectorAbierto(false);
                entrada.current?.blur();
              }
            }}
            placeholder="Pedir OF: número, artículo, cliente…"
            aria-label="Elegir OF"
          />
          {selectorAbierto ? (
            <div className="monitorof__lista" role="listbox">
              {sugerencias.length === 0 ? (
                <div className="monitorof__opcion muted">Ninguna OF viva coincide con «{consulta}».</div>
              ) : (
                sugerencias.map((r) => (
                  <button
                    key={r.docEntry}
                    type="button"
                    role="option"
                    aria-selected={r.docEntry === visible}
                    className={`monitorof__opcion${r.docEntry === visible ? ' monitorof__opcion--actual' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => elegir(r.docEntry)}
                  >
                    <span className="mono">OF {r.docNum}</span>
                    <span className="monitorof__opcion-articulo">
                      {r.itemCode} · {r.itemDescription}
                    </span>
                    <span className="small muted">
                      {shortDate(r.dueDate)}
                      {r.activeOperators.length > 0 ? ` · ${r.activeOperators.length} dentro` : ''}
                      {r.status !== 'released' ? ' · planificada' : ''}
                    </span>
                  </button>
                ))
              )}
            </div>
          ) : null}
        </div>
        <div className="topbar__spacer" />
        {obsoleto ? (
          <Pill tone="danger">Sin datos nuevos desde hace {Math.round((segundosSinActualizar ?? 0) / 60)} min</Pill>
        ) : null}
        <div className="monitor__reloj mono">{clockTime(reloj)}</div>
        <div className="conn">
          <Dot tone={live && !obsoleto ? 'live' : 'warn'} />
          {live ? 'En vivo' : 'Reconectando'}
        </div>
      </header>

      <div className="content stack">
        {visible === null ? (
          <SinDatos texto={updatedAt ? 'No hay ninguna OF viva en SAP.' : 'Cargando órdenes…'} />
        ) : (
          <>
            <section className="card monitorof__cabecera">
              <div className="monitorof__modo">
                {enOrigen ? (
                  <Pill tone="accent">OF prioritaria · entrega más cercana a hoy</Pill>
                ) : (
                  <>
                    <Pill tone="warn">Elegida a mano</Pill>
                    <span className="small muted">
                      Vuelve sola a la OF prioritaria en <b className="mono">{quedan}</b> s sin tocar nada
                    </span>
                    <button className="btn btn--ghost" onClick={() => setElegida(null)}>
                      Volver ya
                    </button>
                  </>
                )}
              </div>
              <div className="monitorof__titular">
                <div style={{ minWidth: 0 }}>
                  <div className="monitorof__docnum mono">OF {orden?.docNum ?? fila?.docNum ?? '…'}</div>
                  <div className="monitorof__articulo">
                    <span className="mono">{orden?.itemCode ?? fila?.itemCode}</span>{' '}
                    {orden?.itemDescription ?? fila?.itemDescription}
                  </div>
                  <div className="small muted">
                    {[orden?.customerName ?? fila?.customerName, orden?.project ?? fila?.project]
                      .filter(Boolean)
                      .join(' · ') || 'Sin cliente ni proyecto'}
                  </div>
                </div>
                <div className="monitorof__datos">
                  <div>
                    <div className="eyebrow">Entrega</div>
                    <div className="monitorof__dato">{shortDate(entrega)}</div>
                    <Pill tone={tonoEntrega}>{textoEntrega(entrega, reloj)}</Pill>
                  </div>
                  <div>
                    <div className="eyebrow">Fabricado</div>
                    <div className="monitorof__dato mono">
                      {qty(orden?.completedQuantity ?? fila?.completedQuantity ?? 0)} /{' '}
                      {qty(orden?.plannedQuantity ?? fila?.plannedQuantity ?? 0)}
                    </div>
                  </div>
                  <div>
                    <div className="eyebrow">Dentro ahora</div>
                    <div className={`monitorof__dato${dentro.length > 0 ? ' monitorof__dato--ok' : ''}`}>
                      {ficha ? dentro.length : '…'}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="monitor__columnas">
              <section className="card">
                <div className="eyebrow" style={{ marginBottom: 10 }}>Personas dentro de la OF</div>
                {cargandoFicha && !ficha ? (
                  <SinDatos texto="Cargando…" />
                ) : !ficha ? (
                  <SinDatos texto="Esta OF no está en la copia local; puede que aún no se haya sincronizado con SAP." />
                ) : dentro.length === 0 ? (
                  <SinDatos
                    texto={
                      ultimo
                        ? `Ahora mismo no hay nadie. El último fue ${ultimo.nombre}, en ${ultimo.fase}, ${desdeLegible(ultimo.hasta ?? ultimo.desde, reloj)}.`
                        : 'Ahora mismo no hay nadie, y nadie ha fichado todavía en esta OF.'
                    }
                  />
                ) : (
                  <div className="monitorof__tabla-scroll">
                    <table className="table table--zebra monitorof__tabla">
                      <thead>
                        <tr>
                          <th>Usuario</th>
                          <th>Desde</th>
                          <th>Tiempo</th>
                          <th>Artículo</th>
                          <th>Fase</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dentro.map((t) => (
                          <tr key={t.id}>
                            <td>
                              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                                <Dot tone={t.causa ? 'danger' : 'live'} />
                                <div>
                                  <div className="monitorof__nombre">{t.nombre}</div>
                                  {t.causa ? <div className="small monitorof__parado">Parado · {t.causa}</div> : null}
                                </div>
                              </div>
                            </td>
                            <td className="mono">{desdeLegible(t.desde, reloj)}</td>
                            <td>
                              <LiveTimer startedAt={t.desde} className="monitor__cronometro" />
                            </td>
                            <td>
                              <div className="mono">{ficha.orden.itemCode}</div>
                              <div className="small muted">{ficha.orden.itemDescription}</div>
                            </td>
                            <td>
                              <div className="monitorof__fase">{t.fase}</div>
                              {t.recurso ? <div className="small muted">{t.recurso}</div> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="stack">
                <div className="card">
                  <div className="eyebrow" style={{ marginBottom: 10 }}>Otras OFs con gente dentro</div>
                  {otrasConGente.length === 0 ? (
                    <div className="small muted">Ninguna otra OF tiene a nadie ahora mismo.</div>
                  ) : (
                    <div className="stack" style={{ gap: 6 }}>
                      {otrasConGente.map((r) => (
                        <button key={r.docEntry} type="button" className="monitorof__otra" onClick={() => elegir(r.docEntry)}>
                          <span className="mono">OF {r.docNum}</span>
                          <span className="small muted monitorof__otra-gente">{r.activeOperators.join(', ')}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="card">
                  <div className="eyebrow" style={{ marginBottom: 10 }}>Ya han salido hoy</div>
                  {salidasHoy.length === 0 ? (
                    <div className="small muted">Nadie ha cerrado un tramo hoy en esta OF.</div>
                  ) : (
                    <div className="stack" style={{ gap: 8 }}>
                      {salidasHoy.map((t) => (
                        <div key={t.id} className="row" style={{ gap: 10, justifyContent: 'space-between' }}>
                          <div style={{ minWidth: 0 }}>
                            <div>{t.nombre}</div>
                            <div className="small muted">
                              {t.fase} · {desdeLegible(t.desde, reloj)}–{t.hasta ? desdeLegible(t.hasta, reloj) : '…'}
                              {t.causa ? ` · ${t.causa}` : ''}
                            </div>
                          </div>
                          <span className="mono small">{hhmm(t.segundos)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
